import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from '~/shadcn/ui/button/button'
import { Field, FieldError, FieldGroup, FieldLabel } from '~/shadcn/ui/field'
import {
  Form,
  FormDescription,
  FormField,
  FormItem,
} from '~/shadcn/ui/form/form'
import { Spinner } from '~/shadcn/ui/spinner'
import { Input } from '~/shadcn/ui/input'
import { HeadingTwo } from '~/@mid_ui/typography/HeadingTwo'
import { Text } from '~/@mid_ui/typography/Text'
import { LoginWithGoogleButton } from '~/@pages_auth/google-login/login-with-google-button/LoginWithGoogleButton'
import { isSocialNetworkInAppWebView } from '~/@lib/utils/function.utils'
import { registerSchema, type RegisterSchema } from './registerSchema'
import type { RegisterRequest } from './useRegister'
import { GdprCheckBoxFields } from './GdprCheckBoxFields'
import { AmbassadorInviteBanner } from '~/@biz_ambassador/claim/AmbassadorInviteBanner'
import { useAmbassadorCode } from '~/@biz_ambassador/code/useAmbassadorCode'
import { usePublicAmbassadorInfo } from '~/@biz_ambassador/claim/usePublicAmbassadorInfo'
import { useSettings } from '~/@mid_settings/settings'
import { TurnstileWidget } from '~/@lib_integrations/turnstile/TurnstileWidget'
import { useTurnstileToken } from '~/@lib_integrations/turnstile/useTurnstileToken'

type RegisterFormProps<T> = {
  handleSubmitForm: (props: T) => void
  isRequesting: boolean
  error: string | null
  title?: string
  description?: string
}

function RegisterForm(props: RegisterFormProps<RegisterRequest>) {
  const { turnstile } = useSettings()
  const { ambassadorCode } = useAmbassadorCode()
  const { data: ambassadorInfo } = usePublicAmbassadorInfo(ambassadorCode)
  const {
    turnstileRef,
    requestToken,
    markReady,
    reset: resetTurnstile,
  } = useTurnstileToken()
  const [isVerifying, setIsVerifying] = useState(false)

  const form = useForm<RegisterSchema>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: '',
      password: '',
      lang: 'sk-SK',
      gdpr: false,
      marketing: false,
      bonusCode: '',
      tokenAffilRecommender: '',
    },
  })

  useEffect(() => {
    if (props.error != null) {
      resetTurnstile()
    }
  }, [props.error, resetTurnstile])

  async function onSubmit(data: RegisterSchema) {
    try {
      setIsVerifying(true)

      let turnstileToken: string | undefined
      if (turnstile.siteKey) {
        turnstileToken = await requestToken()
      }

      props.handleSubmitForm({
        ...data,
        turnstileToken,
      })
    } catch {
      form.setError('root', {
        message: 'Overenie zlyhalo. Skúste to znova.',
      })
      resetTurnstile()
    } finally {
      setIsVerifying(false)
    }
  }

  const isSubmitting = props.isRequesting || isVerifying

  return (
    <div className="px-5 pt-10 pb-20">
      <Field className="flex flex-col items-start gap-5 mb-10">
        {ambassadorInfo && (
          <AmbassadorInviteBanner
            reference={ambassadorInfo.reference}
            description="Zaregistrujte sa a váš ambasádor vám pomôže zorientovať sa vo svete investícií."
          />
        )}

        <HeadingTwo className="text-4xl font-bold">
          {props.title ?? 'Vytvoriť účet'}
        </HeadingTwo>
        <Text className="text-base font-medium leading-normal">
          {props.description ??
            'Registrujte sa s vaším emailom alebo využite prihlásenie cez váš Google účet.'}
        </Text>
      </Field>

      {!isSocialNetworkInAppWebView() && (
        <Field className="flex flex-col items-start gap-4 mb-10">
          <LoginWithGoogleButton />
        </Field>
      )}

      <Field className="flex flex-col items-start gap-8 mb-10">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FieldGroup>
              <FormField
                control={form.control}
                name="email"
                render={({ fieldState, field }) => (
                  <FormItem>
                    <FieldLabel htmlFor="email">Váš e-mail</FieldLabel>
                    <Input
                      aria-invalid={!!fieldState.error}
                      id="email"
                      type="text"
                      autoComplete="off"
                      {...field}
                    />
                    {fieldState.error && (
                      <FieldError>{fieldState.error.message}</FieldError>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ fieldState, field }) => (
                  <FormItem>
                    <div className="flex items-center">
                      <FieldLabel htmlFor="password">Heslo</FieldLabel>
                    </div>
                    <Input
                      aria-invalid={!!fieldState.error}
                      id="password"
                      type="password"
                      autoComplete="off"
                      {...field}
                    />
                    {fieldState.error ? (
                      <FieldError>
                        Najmenej 8 znakov, veľké a malé písmená a číslo.
                      </FieldError>
                    ) : (
                      <FormDescription>
                        Najmenej 8 znakov, veľké a malé písmená a číslo.
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              <GdprCheckBoxFields
                control={form.control}
                isRequesting={props.isRequesting}
              />

              {form.formState.errors.root && (
                <FieldError className="text-sm font-medium text-destructive">
                  {form.formState.errors.root.message}
                </FieldError>
              )}

              {props.error != null && (
                <FieldError className="text-sm font-medium text-destructive">
                  {props.error}
                </FieldError>
              )}

              <TurnstileWidget
                ref={turnstileRef}
                action="register"
                onWidgetLoad={markReady}
                onExpire={resetTurnstile}
              />

              <Field className="mt-4">
                <Button
                  variant="accent"
                  size="lg"
                  type="submit"
                  disabled={isSubmitting}>
                  {isSubmitting ? (
                    <Spinner className="size-4" />
                  ) : (
                    'Registrácia'
                  )}
                </Button>
              </Field>
            </FieldGroup>
          </form>

          <div className="inline-flex flex-row items-center justify-center gap-2">
            Máte už účet?{' '}
            <Link to="/login" className="text-accent">
              Prihláste sa
            </Link>
          </div>
        </Form>
      </Field>
    </div>
  )
}

export { type RegisterFormProps, RegisterForm }
